import { v4 as uuidv4 } from 'uuid';

export const ANONYMOUS_ID_STORAGE_KEY = 'trivia_anonymous_id';
export const ANONYMOUS_IDENTITY_CLAIMED_EVENT = 'atrivial:anonymous-identity-claimed';
const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PARENT_IDENTITY_PARAM = 'anonymous_identity';
const EMBED_PARENT_ORIGIN_PARAM = 'embed_parent_origin';

let runtimeAnonymousId: string | null = null;
const listeners = new Set<(anonymousId: string | null) => void>();
const pendingRotations = new Map<string, Promise<string>>();
const rotationCandidates = new Map<string, string>();
const completedRotations = new Map<string, string>();
export type AnonymousIdentityResolutionFailure = 'claim_settlement_retryable';
let resolutionFailure: AnonymousIdentityResolutionFailure | null = null;
const resolutionFailureListeners = new Set<() => void>();

function emitAnonymousIdentity(anonymousId: string | null): void {
  for (const listener of listeners) listener(anonymousId);
}

function setRuntimeAnonymousId(anonymousId: string | null): void {
  if (runtimeAnonymousId === anonymousId) return;
  runtimeAnonymousId = anonymousId;
  emitAnonymousIdentity(anonymousId);
}

function assertAnonymousId(anonymousId: string): void {
  if (!UUID_V4_PATTERN.test(anonymousId)) {
    throw new Error('Anonymous player identity must be a UUID v4');
  }
}

/** Returns the browser identity used to persist anonymous player progress. */
export function getAnonymousId(): string | null {
  if (typeof window === 'undefined') return runtimeAnonymousId;
  if (runtimeAnonymousId) return runtimeAnonymousId;
  try {
    const storedId = localStorage.getItem(ANONYMOUS_ID_STORAGE_KEY);
    if (storedId && UUID_V4_PATTERN.test(storedId)) runtimeAnonymousId = storedId;
  } catch {
    // The in-memory identity still keeps one embedded playthrough coherent.
  }
  return runtimeAnonymousId;
}

/** Makes one anonymous identity canonical for gameplay, analytics, and later claiming. */
export function rememberAnonymousId(anonymousId: string): void {
  assertAnonymousId(anonymousId);
  try {
    localStorage.setItem(ANONYMOUS_ID_STORAGE_KEY, anonymousId);
  } catch {
    // Runtime identity remains available when third-party storage is unavailable.
  }
  setRuntimeAnonymousId(anonymousId);
}

/** Adopts the value already written by another browsing context without echoing it back. */
export function adoptAnonymousIdFromStorage(anonymousId: string): void {
  assertAnonymousId(anonymousId);
  setRuntimeAnonymousId(anonymousId);
}

export function subscribeAnonymousIdentity(
  listener: (anonymousId: string | null) => void
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getAnonymousIdentityResolutionFailure(): AnonymousIdentityResolutionFailure | null {
  return resolutionFailure;
}

export function subscribeAnonymousIdentityResolutionFailure(listener: () => void): () => void {
  resolutionFailureListeners.add(listener);
  return () => resolutionFailureListeners.delete(listener);
}

export function reportAnonymousIdentityResolutionFailure(
  failure: AnonymousIdentityResolutionFailure
): void {
  if (resolutionFailure === failure) return;
  resolutionFailure = failure;
  for (const listener of resolutionFailureListeners) listener();
}

export function clearAnonymousIdentityResolutionFailure(): void {
  if (resolutionFailure === null) return;
  resolutionFailure = null;
  for (const listener of resolutionFailureListeners) listener();
}

/**
 * Clears one exact anonymous identity after its data is claimed.
 * An explicit identity never removes a newer value written by another context.
 */
export function clearAnonymousId(
  expectedAnonymousId?: string,
  replacementAnonymousId?: string
): void {
  const currentAnonymousId = getAnonymousId();
  const claimedAnonymousId = expectedAnonymousId ?? currentAnonymousId;
  try {
    const storedAnonymousId = localStorage.getItem(ANONYMOUS_ID_STORAGE_KEY);
    if (!expectedAnonymousId || storedAnonymousId === expectedAnonymousId) {
      localStorage.removeItem(ANONYMOUS_ID_STORAGE_KEY);
    }
  } catch {
    // Ignore unavailable browser storage.
  }
  if (!expectedAnonymousId || currentAnonymousId === expectedAnonymousId) {
    setRuntimeAnonymousId(null);
  }
  if (typeof window !== 'undefined' && claimedAnonymousId) {
    window.dispatchEvent(
      new CustomEvent(ANONYMOUS_IDENTITY_CLAIMED_EVENT, {
        detail: { anonymousId: claimedAnonymousId, replacementAnonymousId },
      })
    );
  }
}

export function isParentManagedAnonymousIdentity(): boolean {
  if (typeof window === 'undefined' || window.parent === window) return false;
  return new URLSearchParams(window.location.search).get(PARENT_IDENTITY_PARAM) === 'parent';
}

function parseHttpUrl(value: string): URL | null {
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    if (url.origin === 'null' || url.username || url.password) return null;
    return url;
  } catch {
    return null;
  }
}

function parseExactParentOrigin(value: string): string | null {
  const url = parseHttpUrl(value);
  if (!url || value !== url.origin || url.pathname !== '/' || url.search || url.hash) return null;
  return url.origin;
}

/** Resolves the exact configured publisher origin, with a safe legacy referrer fallback. */
export function resolveParentManagedAnonymousIdentityOrigin(): string | null {
  if (typeof window === 'undefined') return null;
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.has(EMBED_PARENT_ORIGIN_PARAM)) {
    return parseExactParentOrigin(searchParams.get(EMBED_PARENT_ORIGIN_PARAM) ?? '');
  }

  if (typeof document === 'undefined' || !document.referrer) return null;
  return parseHttpUrl(document.referrer)?.origin ?? null;
}

/** Returns only the transport fields safe to preserve on another same-frame embed route. */
export function getParentManagedAnonymousIdentityTransportParams(): URLSearchParams | null {
  if (!isParentManagedAnonymousIdentity()) return null;
  const parentOrigin = resolveParentManagedAnonymousIdentityOrigin();
  if (!parentOrigin) return null;
  return new URLSearchParams({
    [PARENT_IDENTITY_PARAM]: 'parent',
    [EMBED_PARENT_ORIGIN_PARAM]: parentOrigin,
  });
}

/** Preserves the validated iframe identity contract on a same-origin app target. */
export function preserveParentManagedIdentityOnInternalTarget(target: string): string {
  if (!target.startsWith('/') || target.startsWith('//')) return target;
  const identityParams = getParentManagedAnonymousIdentityTransportParams();
  if (!identityParams) return target;

  try {
    const destination = new URL(target, window.location.origin);
    if (destination.origin !== window.location.origin) return target;
    destination.searchParams.set('view', 'iframe');
    identityParams.forEach((value, key) => destination.searchParams.set(key, value));
    return `${destination.pathname}${destination.search}${destination.hash}`;
  } catch {
    return target;
  }
}

/**
 * Retires one server-terminal guest identity and returns its replacement.
 * Parent-managed embeds retain their first-party identity bridge; first-party players
 * rotate immediately in the current origin.
 */
export function rotateAnonymousId(retiredId: string): Promise<string> {
  const currentId = getAnonymousId();
  const parentManaged = isParentManagedAnonymousIdentity();
  if (currentId && currentId !== retiredId && !parentManaged) return Promise.resolve(currentId);

  const completedReplacement = completedRotations.get(retiredId);
  if (currentId && currentId === completedReplacement) return Promise.resolve(currentId);

  const pending = pendingRotations.get(retiredId);
  if (pending) return pending;

  const rotation = new Promise<string>((resolve, reject) => {
    if (!parentManaged) {
      const replacementId = uuidv4();
      rememberAnonymousId(replacementId);
      resolve(replacementId);
      return;
    }

    const replacementId = rotationCandidates.get(retiredId) ?? uuidv4();
    rotationCandidates.set(retiredId, replacementId);

    const unsubscribe = subscribeAnonymousIdentity((nextId) => {
      if (nextId !== replacementId) return;
      clearTimeout(timeoutId);
      unsubscribe();
      completedRotations.set(retiredId, replacementId);
      rotationCandidates.delete(retiredId);
      clearAnonymousIdentityResolutionFailure();
      resolve(nextId);
    });
    const timeoutId = setTimeout(() => {
      unsubscribe();
      reportAnonymousIdentityResolutionFailure('claim_settlement_retryable');
      reject(new Error('Parent-managed anonymous identity replacement was not acknowledged'));
    }, 2000);
    clearAnonymousId(retiredId, replacementId);
  });

  pendingRotations.set(retiredId, rotation);
  const clearPendingRotation = () => pendingRotations.delete(retiredId);
  void rotation.then(clearPendingRotation, clearPendingRotation);
  return rotation;
}
